import type { MouseEvent, ReactNode } from "react"
import ReactMarkdown from "react-markdown"
import { router, usePage } from "@inertiajs/react"
import { trackConversion } from "@/lib/analytics"
import { stripLocale } from "@/lib/locale-path"
import type { PageProps } from "@/types/inertia"

// Renders one assistant reply. Markdown is limited to prose, lists and
// links — no headings, images or raw HTML, whatever the model sends.
// Links do three different things: same-site links navigate through
// Inertia, links to the other brand carry the conversation across (see
// useAssistant.createHandoff), and tel:/mailto: links behave normally.

const ALLOWED = ["p", "a", "ul", "ol", "li", "strong", "em", "br"]

// Paths that mean the assistant handed the visitor to a form or booking —
// tracked as assistant_handoff on /admin/assistant (not a funnel action).
const INTENT_PATHS = ["/book", "/contact", "/rent-a-room", "/locations"]

type Props = {
  content: string
  createHandoff: () => Promise<string | null>
  onNavigate?: () => void
}

export function AssistantMessage({ content, createHandoff, onNavigate }: Props) {
  const { brand_urls, current_brand, locale_slugs } = usePage<PageProps>().props
  const sanctuary_locations = usePage<PageProps>().props.sanctuary_locations ?? []

  function AssistantLink({ href, children }: { href?: string; children?: ReactNode }) {
    const onClick = async (event: MouseEvent<HTMLAnchorElement>) => {
      if (!href) return
      if (href.startsWith("tel:") || href.startsWith("mailto:")) {
        trackConversion("assistant_handoff")
        return
      }

      let url: URL
      try {
        url = new URL(href, window.location.href)
      } catch {
        return
      }
      // The venue's location pages count too — read from the shared prop so
      // a location added in the admin is covered.
      const intentPaths = [...INTENT_PATHS, ...sanctuary_locations.map((l) => l.path)]
      if (intentPaths.includes(stripLocale(url.pathname, locale_slugs))) trackConversion("assistant_handoff")

      if (url.host === window.location.host) {
        event.preventDefault()
        router.visit(url.pathname + url.search + url.hash)
        onNavigate?.()
        return
      }

      const otherRoot = current_brand.key === "gisela" ? brand_urls.sanctuary : brand_urls.gisela
      let otherOrigin: string | null = null
      try {
        otherOrigin = new URL(otherRoot).origin
      } catch {
        otherOrigin = null
      }
      if (otherOrigin && url.origin === otherOrigin) {
        event.preventDefault()
        const token = await createHandoff()
        if (token) url.searchParams.set("assistant", token)
        window.location.assign(url.toString())
      }
    }

    const external = href ? /^https?:/i.test(href) : false
    return (
      <a href={href} onClick={onClick} rel={external ? "noopener" : undefined}>
        {children}
      </a>
    )
  }

  return (
    <div className="assistant-prose">
      <ReactMarkdown allowedElements={ALLOWED} unwrapDisallowed components={{ a: AssistantLink }}>
        {content}
      </ReactMarkdown>
    </div>
  )
}
